import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { registerClient } from "../../api/clientPortalPublic";
import { formatApiError } from "../../utils/formatApiError";

const emptyForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  birthMonth: "",
  birthDay: "",
  birthYear: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PortalRegister() {
  const { salonId } = useOutletContext();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, idx) => String(currentYear - idx)
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!salonId) return;
    setSaving(true);
    setError("");
    try {
      const firstName = form.first_name.trim();
      const lastName = form.last_name.trim();
      const phoneValue = form.phone.trim();
      const emailValue = form.email.trim();
      const birthdayReady =
        form.birthMonth && form.birthDay && form.birthYear;
      const birthday = birthdayReady
        ? `${form.birthYear}-${String(form.birthMonth).padStart(2, "0")}-${String(
            form.birthDay
          ).padStart(2, "0")}`
        : null;
      const payload = {
        first_name: firstName,
        last_name: lastName,
        phone: phoneValue,
        email: emailValue,
        birthday,
      };
      if (!firstName) {
        setError("First name is required.");
        return;
      }
      if (!lastName) {
        setError("Last name is required.");
        return;
      }
      if (!phoneValue || phoneValue.length < 7) {
        setError("Please enter a valid mobile phone number.");
        return;
      }
      if (!emailValue || !emailPattern.test(emailValue)) {
        setError("Please enter a valid email address.");
        return;
      }
      await registerClient(salonId, payload);
      setSuccess(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Portal register error:", err);
      }
      if (err?.response?.status === 409) {
        setDuplicateEmail(true);
        setError("");
        return;
      }
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          Thanks! Please check your email for instructions to create your
          username and password.
        </h2>
      </div>
    );
  }

  if (duplicateEmail) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          Email needs to be updated
        </h2>
        <p className="mt-2 text-sm md:text-base text-slate-600">
          This email is used by more than one client profile at this salon.
          Please contact the salon to update your email before activating your
          online account.
        </p>
        <div className="mt-4">
          <Link
            to={`/portal/salon/${salonId}/login`}
            className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Online Scheduling Signup
          </h2>
          <p className="mt-1 text-sm md:text-base text-slate-600">
            Welcome! Create your account in under a minute. We'll review your
            request and activate your online booking access as soon as possible.
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-600">
              First Name
            </label>
            <input
              type="text"
              value={form.first_name}
              onChange={handleChange("first_name")}
              className="h-11 w-full rounded-xl border border-slate-300 px-4"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-600">
              Last Name
            </label>
            <input
              type="text"
              value={form.last_name}
              onChange={handleChange("last_name")}
              className="h-11 w-full rounded-xl border border-slate-300 px-4"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-600">
              Mobile Phone
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={handleChange("phone")}
              className="h-11 w-full rounded-xl border border-slate-300 px-4"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-600">
              Email Address
            </label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              className="h-11 w-full rounded-xl border border-slate-300 px-4"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-600">
              Birthdate (optional)
            </label>
            <div className="grid grid-cols-3 gap-3">
              <select
                value={form.birthMonth}
                onChange={handleChange("birthMonth")}
                className="h-11 w-full rounded-xl border border-slate-300 px-3"
              >
                <option value="">Month</option>
                {Array.from({ length: 12 }, (_, idx) => String(idx + 1)).map(
                  (value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  )
                )}
              </select>
              <select
                value={form.birthDay}
                onChange={handleChange("birthDay")}
                className="h-11 w-full rounded-xl border border-slate-300 px-3"
              >
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, idx) => String(idx + 1)).map(
                  (value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  )
                )}
              </select>
              <select
                value={form.birthYear}
                onChange={handleChange("birthYear")}
                className="h-11 w-full rounded-xl border border-slate-300 px-3"
              >
                <option value="">Year</option>
                {years.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Submitting..." : "Create Account"}
            </button>
            <Link
              to={`/portal/salon/${salonId}`}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
